import { useRef, useState } from 'react';
import { addDays, formatDe, parseKeypadDigits, todayIn } from '@frisk/shared';
import { kicker, t } from '../theme';
import { useBatch, useStore } from '../store';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'] as const;

/**
 * The single commit point. Everything scanned, spoken or tapped lands here
 * before it reaches the database, which is what makes an imperfect transcript
 * a correction rather than bad data.
 */
export function Confirm() {
  const draft = useStore((s) => s.draft);
  const batch = useBatch();
  const { patchDraft, saveDraft, discardDraft, openPrompt, flash } = useStore.getState();

  const [digits, setDigits] = useState('');
  const [saving, setSaving] = useState(false);
  /**
   * Mirrors `digits` so two taps landing in the same tick both see the latest
   * value. Reading the state variable directly loses keystrokes on a fast
   * double-tap, because every handler in that tick closes over the old value.
   */
  const digitsRef = useRef('');

  const writeDigits = (next: string) => {
    digitsRef.current = next;
    setDigits(next);
  };

  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);
  if (!draft) return null;

  const partialDate = () => {
    if (!digits) return draft.bestBefore ? formatDe(draft.bestBefore) : '– – . – – . – – – –';
    let out = digits.slice(0, 2);
    if (digits.length > 2) out += '.' + digits.slice(2, 4);
    else if (digits.length === 2) out += '.';
    if (digits.length > 4) out += '.' + digits.slice(4, 8);
    else if (digits.length === 4) out += '.';
    return out;
  };

  const invalid = digits.length === 8 && parseKeypadDigits(digits) === null;
  /**
   * A half-typed or impossible date must not be saveable: the field would show
   * one thing while the previously-parsed value silently went to the database.
   */
  const incomplete = digits.length > 0 && digits.length < 8;
  const blocked = saving || invalid || incomplete;

  const pressKey = (key: string) => {
    if (key === 'C') {
      writeDigits('');
      patchDraft({ bestBefore: null });
      return;
    }
    if (key === '⌫') {
      writeDigits(digitsRef.current.slice(0, -1));
      return;
    }
    if (digitsRef.current.length >= 8) return;

    const next = digitsRef.current + key;
    writeDigits(next);
    if (next.length === 8) {
      const parsed = parseKeypadDigits(next);
      if (parsed) patchDraft({ bestBefore: parsed });
      else flash('Kein gültiges Datum');
    }
  };

  const shift = (days: number) => {
    const base = draft.bestBefore ?? today;
    writeDigits('');
    patchDraft({ bestBefore: addDays(base, days) });
  };

  const save = async (then: 'more' | 'done') => {
    setSaving(true);
    try {
      await saveDraft(then);
      writeDigits('');
    } catch {
      flash('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const field: React.CSSProperties = {
    borderRadius: 22,
    background: t.surface,
    border: t.hairline,
    padding: '16px 22px',
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '34px 38px 26px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={kicker}>PRÜFEN &amp; SPEICHERN</div>
          <div style={{ fontSize: 44, fontWeight: 500, letterSpacing: '-0.045em', marginTop: 6, whiteSpace: 'nowrap' }}>
            Stimmt das so?
          </div>
        </div>
        {batch.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderRadius: 999, background: 'oklch(0.23 0.012 60)', fontSize: 19, fontWeight: 500, color: 'oklch(0.82 0.015 70)' }}>
            {batch.length} in dieser Runde
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.22fr 0.78fr', gap: 20, marginTop: 22 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          <div
            className="press"
            onClick={() =>
              openPrompt({
                label: 'PRODUKT',
                value: draft.name,
                onCommit: (name) => {
                  useStore.setState({ prompt: null });
                  if (name) patchDraft({ name });
                },
              })
            }
            style={{ ...field, display: 'flex', alignItems: 'center', gap: 16 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>PRODUKT</div>
              <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 4, textWrap: 'pretty', overflowWrap: 'anywhere' }}>
                {draft.name}
              </div>
            </div>
            <div style={{ fontSize: 18, color: t.inkMuted }}>tippen</div>
          </div>

          <div style={{ ...field, display: 'flex', alignItems: 'center', gap: 18 }}>
            <div
              style={{
                width: 70,
                height: 70,
                flex: 'none',
                borderRadius: 16,
                background: draft.imageUrl
                  ? `center / cover no-repeat url(${draft.imageUrl})`
                  : 'repeating-linear-gradient(135deg, oklch(0.3 0.012 60) 0 8px, oklch(0.26 0.012 60) 8px 16px)',
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>BILD</div>
              <div style={{ fontSize: 20, fontWeight: 500, marginTop: 5 }}>
                {draft.imageUrl ? 'Aus Produktdatenbank' : 'Noch kein Bild'}
              </div>
            </div>
          </div>

          <div style={{ ...field, border: invalid ? '1px solid oklch(0.72 0.17 25 / 0.6)' : t.hairline }}>
            <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: invalid ? 'oklch(0.78 0.14 30)' : t.accent }}>
              {invalid ? 'UNGÜLTIGES DATUM' : incomplete ? 'DATUM UNVOLLSTÄNDIG' : draft.bestBefore ? 'HALTBAR BIS' : 'KEIN DATUM'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 42, fontWeight: 500, letterSpacing: '-0.045em', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
              {partialDate()}
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
              <div className="press" onClick={() => shift(1)} style={{ padding: '11px 18px', borderRadius: 999, background: t.control, fontSize: 18, fontWeight: 500 }}>
                +1 Tag
              </div>
              <div className="press" onClick={() => shift(7)} style={{ padding: '11px 18px', borderRadius: 999, background: t.control, fontSize: 18, fontWeight: 500 }}>
                +1 Woche
              </div>
              <div
                className="press"
                onClick={() => {
                  writeDigits('');
                  patchDraft({ bestBefore: null });
                }}
                style={{ padding: '11px 18px', borderRadius: 999, border: '1px solid oklch(1 0 0 / 0.12)', fontSize: 18, fontWeight: 500, color: 'oklch(0.75 0.015 70)' }}
              >
                kein Datum
              </div>
            </div>
          </div>

          <div style={{ ...field, padding: '14px 22px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>MENGE</div>
            <div style={{ flex: 1 }} />
            <div className="press" onClick={() => patchDraft({ qty: Math.max(1, draft.qty - 1) })} style={{ width: 56, height: 56, borderRadius: 999, background: t.control, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
              –
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, minWidth: 44, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{draft.qty}</div>
            <div className="press" onClick={() => patchDraft({ qty: Math.min(99, draft.qty + 1) })} style={{ width: 56, height: 56, borderRadius: 999, background: t.control, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
              +
            </div>
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', gap: 12, opacity: saving ? 0.6 : 1 }}>
            <div className="press" onClick={discardDraft} style={{ height: 78, width: 128, flex: 'none', borderRadius: 20, border: t.hairlineStrong, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 500, color: 'oklch(0.8 0.015 70)', whiteSpace: 'nowrap' }}>
              Abbrechen
            </div>
            <div
              className={blocked ? undefined : 'press'}
              onClick={() => !blocked && void save('done')}
              style={{ height: 78, flex: 1, borderRadius: 20, background: 'oklch(0.3 0.012 60)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 600, whiteSpace: 'nowrap', opacity: blocked ? 0.4 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}
            >
              Einräumen &amp; fertig
            </div>
            <div
              className={blocked ? undefined : 'press'}
              onClick={() => !blocked && void save('more')}
              style={{ height: 78, flex: 1.15, borderRadius: 20, background: t.accent, color: t.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 21, fontWeight: 600, whiteSpace: 'nowrap', opacity: blocked ? 0.4 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}
            >
              Einräumen &amp; weiter
            </div>
          </div>
        </div>

        <div style={{ borderRadius: 26, background: t.surfaceSunken, border: t.hairline, padding: 18, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(4, 1fr)', gap: 12 }}>
          {KEYS.map((key) => (
            <div
              key={key}
              className="press"
              onClick={() => pressKey(key)}
              style={{ borderRadius: 18, background: t.control, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 500 }}
            >
              {key}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
